import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Input, Modal, Button, Tooltip, Select } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import {
  bytesFormat,
  dateFormat,
  extractDateInISOFormat,
  getDateInISOFormat,
  getDateToBRPattern,
  getDateWithISOFormat,
} from '@utils/formatters';
import ActionTypes from '@utils/deviceActions';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { StatusItemProps, StatusItemType } from '@components/StatusMenu';
import { filterColumnsByData } from '@utils/filterColumnsByData';
import BlockStatusTag from '@components/BlockStatusTag';
import SelectCustomers from '@components/SelectCustomers';

import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import useSearchParams from '@hooks/useSearchParams';
import moment from 'moment';
import { IObject } from '@utils/defaultTypes';
import { ITableFilters } from '@components/Filters';
import { GroupedStatusItem } from '@components/GroupedStatusMenu';
import { useNavigate } from 'react-router-dom';
import RequestReport from '@components/RequestReport';
import rolesConstants from '@utils/rolesConstants';
import CustomerPermissionsList from '@components/CustomerPermissionsList';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { IAction } from './types';

const { Search } = Input;

interface IPagination {
  page?: number | string;
  linesPerPage?: number | string;
}

interface IActionsQueue {
  defaultFilters?: ITableFilters[];
  defaultFiltersValues?: IObject;
  defaultColumns?: string[];
  tableOnly?: boolean;
  tableOnlyKey?: string;
  defaultPagination?: IPagination;
  operationType?: string;
  setDefaultPaginationChange?: (pagination: IPagination) => void;
}

const ActionsQueue = ({
  defaultFilters,
  defaultFiltersValues,
  defaultColumns,
  tableOnly,
  defaultPagination,
  setDefaultPaginationChange,
  tableOnlyKey,
  operationType,
}: IActionsQueue) => {
  const { t } = useTranslation([
    'actions-queue',
    'device-actions',
    'auto-services',
    'permissions',
  ]);
  const navigate = useNavigate();
  const {
    isRoot,
    isMotBusiness,
    selectedApp,
    hasCustomersRoles,
    isMot,
    theme,
    customer: { getCustomerById },
  } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const isBroker = ['ROOT', 'SUPERBROKER', 'BROKER'].includes(
    customerType ?? '',
  );

  const [msisdnValue, setMsisdnValue] = useState<string>();
  const [iccidValue, setIccidValue] = useState<string>();
  const [imsiValue, setImsiValue] = useState<string>();
  const [contractValue, setContractValue] = useState<string>();
  const [createdByValue, setCreatedByValue] = useState<string>();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['action', 'status', 'startDate', 'endDate'],
    extraParams: [
      'msisdn',
      'iccid',
      'imsi',
      'contract',
      'createdBy',
      'customerId',
    ],
  });

  const {
    startDate,
    endDate,
    action,
    msisdn,
    iccid,
    imsi,
    contract,
    createdBy,
    customerId,
    status,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    if (!startDate && !endDate && !tableOnly) {
      const lastWeek = moment().subtract(7, 'days').format('YYYYMMDD');
      const today = moment().format('YYYYMMDD');

      setSearchParams({
        startDate: lastWeek,
        endDate: today,
      });
    }
  }, [endDate, setSearchParams, startDate, tableOnly]);

  useEffect(() => {
    setMsisdnValue(msisdn);
  }, [msisdn]);

  useEffect(() => {
    setIccidValue(iccid);
  }, [iccid]);

  useEffect(() => {
    setImsiValue(imsi);
  }, [imsi]);

  useEffect(() => {
    setContractValue(contract);
  }, [contract]);

  useEffect(() => {
    setCreatedByValue(createdBy);
  }, [createdBy]);

  useEffect(() => {
    setCreatedByValue(createdBy);
  }, [createdBy]);

  const defaultParams = useMemo(
    () => ({
      startDate,
      endDate,
      msisdn,
      iccid,
      imsi,
      contract,
      createdBy,
      customerId,
      product: selectedApp?.toUpperCase(),
      ...defaultFiltersValues,
    }),
    [
      contract,
      createdBy,
      customerId,
      defaultFiltersValues,
      endDate,
      iccid,
      imsi,
      msisdn,
      selectedApp,
      startDate,
    ],
  );

  const { data } = useSwr(
    '/service-proxy/actions',
    {
      params: {
        action,
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
        status,
        ...defaultParams,
      },
    },
    { refreshInterval: 10 * 1000 },
  );

  const { data: dataCountByStatus } = useSwr(
    action || defaultFiltersValues?.action
      ? '/service-proxy/actions/count-by-status'
      : null,
    {
      params: {
        action,
        ...defaultParams,
      },
    },
    { refreshInterval: 10 * 1000 },
  );

  const { data: dataCountByType } = useSwr(
    !tableOnly ? '/service-proxy/actions/count-by-type' : null,
    {
      params: defaultParams,
    },
    {
      refreshInterval: 10 * 1000,
    },
  );

  // Removal of unused actions
  const dataCountByTypeFiltered = useMemo(() => {
    const onlyActionsNumber = Object.values(ActionTypes)?.filter(
      item => typeof item === 'number',
    );
    return dataCountByType?.filter(({ item }: { item: string }) =>
      onlyActionsNumber.includes(Number(item)),
    );
  }, [dataCountByType]);

  // convert processData attr in new names, 'actionsWithProcessDataToShow'
  const formattedData = useMemo(() => {
    if (data) {
      const actionsWithProcessDataToShow = {
        16: 'nickname',
        20: 'networkBlockType',
        21: 'networkUnlockDate',
        22: 'imei',
        23: 'imei',
        24: 'suspensionDate',
        25: 'suspensionDate',
        26: 'newIccid',
        28: 'newMsisdn',
        31: 'blockedCustomer',
        32: 'unlockedCustomer',
        33: 'creditAdded',
        34: 'newIccid',
        52: 'cancellationType',
        53: 'targetCustomer',
        54: 'targetCustomer',
      };

      return data.content?.map((currentAction: any) => {
        if (currentAction.processData) {
          const dataInProcessDataName =
            // @ts-ignore
            actionsWithProcessDataToShow[currentAction.actionType];

          if (dataInProcessDataName) {
            const { processData, ...restAction } = currentAction;
            return {
              ...restAction,
              [dataInProcessDataName]: processData,
            };
          }
        }

        return currentAction;
      });
    }
    return [];
  }, [data]);

  const extraInformationForMultiConnectActivation = useCallback(
    (record: IAction, statusLabel: string) => {
      if (
        record.flagProcessed === 99 ||
        record.flagProcessed === 98 ||
        record.flagProcessed === 92 ||
        (record.flagProcessed >= 50 && record.flagProcessed <= 60)
      ) {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>{t('device-actions:status.error')}</div>
            {isRoot && (
              <Button
                type="link"
                size="small"
                icon={
                  <QuestionCircleOutlined
                    style={{
                      display: 'inline',
                    }}
                  />
                }
                onClick={() => {
                  Modal.error({
                    title: t('device-actions:status.error'),
                    closable: true,
                    maskClosable: true,
                    content: (
                      <div
                        style={{
                          marginTop: 10,
                          background: theme?.colors?.background1,
                          padding: '8px 10px',
                          borderRadius: 4,
                        }}
                      >
                        {record.processLog}
                      </div>
                    ),
                  });
                }}
              />
            )}
          </div>
        );
      }
      return statusLabel;
    },
    [isRoot, t, theme?.colors?.background1],
  );

  const formatOperator = useCallback(
    (operators: string) =>
      operators.replaceAll(',', ' ').replace('[', '').replace(']', ''),
    [],
  );

  const handleNetworkBlockType = useCallback((value: any) => {
    if (value) {
      const { tpBloqueio, dtInicioBloqueio, dtFimBloqueio } = JSON.parse(value);

      return (
        <BlockStatusTag
          blockType={tpBloqueio}
          blockingInitialDate={dtInicioBloqueio}
          blockingFinalDate={dtFimBloqueio}
        />
      );
    }

    return <></>;
  }, []);

  const handleSuspensionDate = useCallback(
    (value: any) => {
      if (value) {
        const { dtInicioSuspensao, dtTerminoSuspensao } = JSON.parse(value);

        const start =
          dtInicioSuspensao && getDateToBRPattern(dtInicioSuspensao);
        const end =
          dtTerminoSuspensao && getDateToBRPattern(dtTerminoSuspensao);

        if (start && end) {
          return `${start} ${t('to')} ${end}`;
        }
        if (start) {
          return `${t('from')} ${start}`;
        }

        return `${t('upUntil')} ${end}`;
      }
      return <></>;
    },
    [t],
  );

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: t('id'),
        dataIndex: 'id',
        align: 'center' as const,
      },
      {
        title: t('actionType'),
        dataIndex: 'actionType',
        render: (value: ActionTypes) => t(`device-actions:labels.${value}`),
      },
      ...(isBroker
        ? [
            {
              title: t('inventory:table.client'),
              dataIndex: 'customer',
              render: (value: any, record: any) => record?.customer?.name,
            },
            {
              title: t('targetCustomer'),
              dataIndex: 'targetCustomer',
              render: (targetCustomer: any, record: any) => {
                const isBatchPermissions = [
                  ActionTypes.ADDING_PERMISSIONS,
                  ActionTypes.REMOVAL_PERMISSIONS,
                ].includes(record.actionType);

                if (!isBatchPermissions) {
                  return <></>;
                }

                const cst = getCustomerById(Number(targetCustomer)) || {};
                return `${cst?.id ?? ''}${cst?.name ? ` - ${cst?.name}` : ''}`;
              },
            },
          ]
        : []),
      {
        title: t('permissions:permissions.title'),
        dataIndex: 'msPayload',
        render: (rolesId: any, record: any) => {
          const isBatchPermissions = [
            ActionTypes.ADDING_PERMISSIONS,
            ActionTypes.REMOVAL_PERMISSIONS,
          ].includes(record.actionType);

          return isBatchPermissions ? (
            <CustomerPermissionsList
              rolesId={rolesId}
              action={record.actionType}
            />
          ) : (
            <></>
          );
        },
      },
      {
        title: t('contract'),
        dataIndex: 'idContract',
        align: 'center' as const,
      },
      {
        title: t('networkBlockType'),
        dataIndex: 'networkBlockType',
        align: 'center' as const,
        render: handleNetworkBlockType,
      },
      {
        title: t('networkUnlockDate'),
        dataIndex: 'networkUnlockDate',
        align: 'center' as const,
        render: (value: any) => {
          if (value) {
            const unlockDate = JSON.parse(value).dtDesbloqueio;
            return extractDateInISOFormat(getDateWithISOFormat(unlockDate));
          }
          return <></>;
        },
      },
      {
        title: t('suspensionDate'),
        dataIndex: 'suspensionDate',
        render: handleSuspensionDate,
      },
      { title: t('imei'), dataIndex: 'imei', align: 'center' as const },
      { title: t('nickname'), dataIndex: 'nickname' },
      {
        title: t('msisdn'),
        dataIndex: 'msisdn',
        align: 'center' as const,
      },
      {
        title: t('newMsisdn'),
        dataIndex: 'newMsisdn',
        align: 'center' as const,
      },
      {
        title: t('imsi'),
        dataIndex: 'imsi',
        align: 'center' as const,
      },
      {
        title: t('iccid'),
        dataIndex: 'iccid',
        align: 'center' as const,
      },
      {
        title: t('newIccid'),
        dataIndex: 'newIccid',
        align: 'center' as const,
      },
      {
        title: t('blockedCustomer'),
        dataIndex: 'blockedCustomer',
        render: (value: any) => value && JSON.parse(value)?.name,
      },
      {
        title: t('unlockedCustomer'),
        dataIndex: 'unlockedCustomer',
        render: (value: any) => value && JSON.parse(value)?.name,
      },
      {
        title: t('operator'),
        dataIndex: 'operator',
        align: 'center' as const,
        render: (value: any, record: any) =>
          record.operator ? formatOperator(record.operator) : record.vendor,
      },
      {
        title: t('processingDate'),
        dataIndex: 'processingDate',
        align: 'center' as const,
        render: (value: string) => value && dateFormat(value),
      },
      {
        title: t('createdAt'),
        dataIndex: 'createdAt',
        render: dateFormat,
      },
      {
        title: t('createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('protocol'),
        dataIndex: 'protocol',
        align: 'center' as const,
      },
      {
        title: t('protocol'),
        dataIndex: 'operationProtocol',
        align: 'center' as const,
      },
      {
        title: t('protocol'),
        dataIndex: 'childProtocol',
        align: 'center' as const,
      },
      {
        title: t('creditAdded'),
        dataIndex: 'creditAdded',
        render: bytesFormat,
        align: 'center' as const,
      },
      {
        title: t('status'),
        dataIndex: 'flagProcessed',
        ...(tableOnly && { width: 250 }),
        render: (value: number, record: IAction) => {
          const isMultiConnectActivation = record.actionType === 0;
          const isSuspension = record.actionType === 9;
          const isSuspensionWithdrawal = record.actionType === 10;
          const showSuspensionWithDrawalStatusDetails =
            isSuspensionWithdrawal && [99, 92].includes(record.flagProcessed);

          const statusLabel = t(
            `device-actions:status.${record.actionType}.${value}`,
          );

          return isRoot &&
            (isMultiConnectActivation ||
              isSuspension ||
              showSuspensionWithDrawalStatusDetails)
            ? extraInformationForMultiConnectActivation(record, statusLabel)
            : statusLabel;
        },
      },
      ...(isMot
        ? [
            {
              title: t('virtualAccountDest'),
              dataIndex: 'virtualAccountDest',
              render: (_: any, record: any) => (
                <Tooltip title={t('view-request')}>
                  <Button
                    type="link"
                    onClick={() => {
                      navigate(
                        `/${selectedApp}/mobile-network/financial/requests/${record?.virtualAccountDest?.id}`,
                      );
                    }}
                  >
                    {record?.virtualAccountDest?.name}
                  </Button>
                </Tooltip>
              ),
            },
            {
              title: t('virtualContractDest'),
              dataIndex: 'virtualContractDest',
              render: (_: any, record: any) => {
                return (
                  <Tooltip title={t('view-contract')}>
                    <Button
                      type="link"
                      onClick={() => {
                        navigate(
                          `/${selectedApp}/mobile-network/financial/contracts/${record?.virtualContractDest?.id}`,
                        );
                      }}
                    >
                      {record?.virtualContractDest?.planDesc}
                    </Button>
                  </Tooltip>
                );
              },
            },
            {
              title: t('inventory:table.client'),
              dataIndex: 'customerDest',
              render: (_: any, record: any) =>
                record?.customerDest?.name && `${record?.customerDest?.name}`,
            },
          ]
        : []),
    ];

    if (defaultColumns) {
      return filterColumnsByData(
        formattedData,
        allColumns.filter(
          column => column && defaultColumns.includes(column.dataIndex),
        ),
      );
    }

    return filterColumnsByData(formattedData, allColumns);
  }, [
    t,
    isRoot,
    isBroker,
    handleNetworkBlockType,
    handleSuspensionDate,
    tableOnly,
    isMot,
    defaultColumns,
    formattedData,
    getCustomerById,
    formatOperator,
    extraInformationForMultiConnectActivation,
    navigate,
    selectedApp,
  ]);

  const tableKey = useMemo(
    () => (action ? `actionsQueue${action}` : 'actionsQueue'),
    [action],
  );

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const sortList = useCallback((list: any) => {
    const orderedList: StatusItemProps[] = list
      // @ts-ignore
      ?.sort((a, b) => a.total - b.total)
      ?.filter((el: StatusItemProps) => !!el);
    orderedList?.reverse();
    return orderedList;
  }, []);

  const actionsList: StatusItemProps[] = useMemo(
    () =>
      sortList(
        dataCountByTypeFiltered?.map(
          ({ item, value }: { item: string; value: number }) => ({
            label: t(`device-actions:labels.${item}`),
            value: item,
            total: value,
            color: 'var(--primary-color)',
          }),
        ),
      ),
    [dataCountByTypeFiltered, sortList, t],
  );

  const getTypeStatus = useCallback((statusNum: string): StatusItemType => {
    const treatedStatus = Number(statusNum);
    if (treatedStatus === 0) {
      return 'success';
    }
    if (treatedStatus < 5) {
      return 'processing';
    }
    return 'error';
  }, []);

  const statusList: GroupedStatusItem[] = useMemo(
    () =>
      dataCountByStatus?.map(
        ({ item, value }: { item: string; value: number }) => ({
          type: getTypeStatus(item),
          label: t(
            `device-actions:status.${
              action || defaultFiltersValues?.action
            }.${item}`,
          ),
          value: item,
          total: value,
        }),
      ),
    [dataCountByStatus, getTypeStatus, t, action, defaultFiltersValues?.action],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles && !tableOnly && (!isMot || isRoot),
        label:
          !isRoot && isMotBusiness
            ? t('inventory:table.department')
            : t('inventory:table.client'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => {
              setSearchParams({ customerId: value });
            }}
            isMotBusiness
          />
        ),
      },
      {
        visible: true,
        label: t('contract'),
        element: (
          <Search
            onSearch={value => setSearchParams({ contract: value })}
            onChange={({ target: { value } }) => setContractValue(value)}
            value={contractValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('msisdn'),
        element: (
          <Search
            onSearch={value => setSearchParams({ msisdn: value })}
            onChange={({ target: { value } }) => setMsisdnValue(value)}
            value={msisdnValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('iccid'),
        element: (
          <Search
            onSearch={value => setSearchParams({ iccid: value })}
            onChange={({ target: { value } }) => setIccidValue(value)}
            value={iccidValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('imsi'),
        element: (
          <Search
            onSearch={value => setSearchParams({ imsi: value })}
            onChange={({ target: { value } }) => setImsiValue(value)}
            value={imsiValue}
            allowClear
          />
        ),
      },

      {
        visible: tableOnly,
        label: t('auto-services:table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                value: 'PROCESSING',
                label: t('auto-services:status.PROCESSING'),
              },
              {
                value: 'PROCESSING_WARNING',
                label: t('auto-services:status.PROCESSING_WARNING'),
              },
              {
                value: 'FINISHED',
                label: t('auto-services:status.FINISHED'),
              },
              {
                value: 'FINISHED_WARNING',
                label: t('auto-services:status.FINISHED_WARNING'),
              },
              {
                value: 'ALL_ERROR',
                label: t('auto-services:status.ALL_ERROR'),
              },
            ]}
            style={{ width: '100%' }}
            value={status}
            onChange={value => setSearchParams({ status: value })}
            allowClear
          />
        ),
      },
      {
        visible: !tableOnly,
        label: t('createdBy'),
        element: (
          <Search
            onSearch={value => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
    ],
    [
      tableOnly,
      t,
      hasCustomersRoles,
      isRoot,
      isMot,
      customerId,
      isMotBusiness,
      contractValue,
      msisdnValue,
      iccidValue,
      imsiValue,
      status,
      createdByValue,
      setSearchParams,
    ],
  );

  const actionsTotal = useMemo(
    () =>
      actionsList?.reduce(
        (accumulator, { total }) => accumulator + (total ?? 0),
        0,
      ),
    [actionsList],
  );

  return (
    <div>
      <Table
        columns={columns}
        data={formattedData || []}
        loading={!data}
        title={t('title')}
        tableKey={tableOnlyKey ?? tableKey}
        filters={{
          list: defaultFilters ?? tableFilters,
          hasValuesInFilters: !tableOnly && hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{
          page,
          linesPerPage,
          total: data?.totalElements,
          ...defaultPagination,
        }}
        extraActions={
          <>
            {!tableOnly && (
              <QuickRangePicker
                onChange={handleDateChange}
                defaultValue={
                  startDate
                    ? [
                        moment(startDate, 'YYYYMMDD'),
                        moment(endDate, 'YYYYMMDD'),
                      ]
                    : undefined
                }
                value={
                  startDate
                    ? [
                        moment(startDate, 'YYYYMMDD'),
                        moment(endDate, 'YYYYMMDD'),
                      ]
                    : undefined
                }
                style={{ maxWidth: 240 }}
              />
            )}
            <RequestReport
              items={[
                {
                  visible: !tableOnly,
                  role: rolesConstants.REQUEST_REPORT,
                  params: defaultParams,
                  path: `/service-proxy/actions/export`,
                  type: `ACTION_QUEUE`,
                  method: 'get',
                },
                {
                  visible: !!tableOnly,
                  role: rolesConstants.REQUEST_REPORT,
                  params: {
                    contract: null,
                    operationProtocol: defaultFiltersValues?.operationProtocol,
                    status: defaultFiltersValues?.status,
                    iccid: defaultParams?.iccid,
                    msisdn: defaultParams?.msisdn,
                    idContract: defaultParams.contract,
                    operationType:
                      defaultFiltersValues?.action || operationType,
                  },
                  path: `/service-proxy/operationDetail/report`,
                  type: `OPERATION_ACTION`,
                  method: 'get',
                },
              ]}
            />
          </>
        }
        onTableChange={setDefaultPaginationChange ?? setSearchParams}
        statusMenu={{
          open: !tableOnly && actionsList?.length > 1,
          itemActive: action,
          list: actionsList,
          onChange: item => {
            setSearchParams({
              action: item.value !== action ? item.value : undefined,
              status: undefined,
            });
          },
          total: {
            value: actionsTotal,
            visible: actionsList?.length > 1,
            title: t('all-actions'),
            onClick: () => clearSearchParams(['action', 'status']),
          },
          showIconClear: true,
        }}
        groupedMenu={{
          open: action && statusList?.length > 1,
          actionActive: action,
          itemActive: status,
          list: statusList,
          onChange: item =>
            setSearchParams({ status: item?.value ?? undefined }),
          showIconClear: true,
        }}
      />
    </div>
  );
};

export default ActionsQueue;
